import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { RowData } from '@scope/scope-one-ui/core/DataTable/DataTable.d';
import { capitalize } from 'lodash';
import { Box, DataTable } from '@scope/scope-one-ui/core';
import { Period } from '@scope/scope-one-ui/core/SentimentBar/SentimentBar.d';
import { EventSidebar } from '../EventSidebar';
import { EsgEventsCard } from '../EsgEventsCard';
import { EsgEventsType, EsgSentimentParams } from '../../types/esgSentiment.d';
import { useGetCompanyInfo, useGetEsgEvents } from '../../services/esgSentiment';
import { cols } from './columns';
import { getTableData } from './utils';

export const ESGEventsSection: FC = () => {
  const { entityId } = useParams<EsgSentimentParams>();
  const [eventSection, setEventSection] = useState<EsgEventsType>('environment');
  const [filter, setFilter] = useState<Record<EsgEventsType, Period>>({
    environment: '7days',
    social: '7days',
    governance: '7days',
  });
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string>();
  const { data: envData, loading: envLoading } = useGetEsgEvents(
    entityId!,
    'E',
    filter.environment,
  );
  const { data: socData, loading: socLoading } = useGetEsgEvents(entityId!, 'S', filter.social);
  const { data: govData, loading: govLoading } = useGetEsgEvents(entityId!, 'G', filter.governance);
  const { data: companyData, loading: companyLoading } = useGetCompanyInfo(entityId!);

  const tableData: Record<EsgEventsType, RowData[]> = {
    environment: getTableData(envData),
    social: getTableData(socData),
    governance: getTableData(govData),
  };

  const onSelectEvent = (id: string) => {
    if (selectedId === id) {
      clearSelection();
      setOpen(false);
    } else {
      setSelectedId(id);
      setOpen(true);
    }
  };

  const toggle = () => setOpen((prev) => !prev);

  const clearSelection = () => setSelectedId('');

  const updateFilter = (type: EsgEventsType, value: Period) =>
    setFilter((prev) => ({ ...prev, [type]: value }));

  useEffect(() => {
    clearSelection();
  }, [filter, eventSection]);

  return (
    <>
      <Box display="flex" gridGap={5.5}>
        <EsgEventsCard
          type="environment"
          activeType={eventSection}
          eventsList={envData}
          loading={envLoading}
          filter={filter.environment}
          onPick={setEventSection}
          onFilter={updateFilter}
        />
        <EsgEventsCard
          type="social"
          activeType={eventSection}
          eventsList={socData}
          loading={socLoading}
          filter={filter.social}
          onPick={setEventSection}
          onFilter={updateFilter}
        />
        <EsgEventsCard
          type="governance"
          activeType={eventSection}
          eventsList={govData}
          loading={govLoading}
          filter={filter.governance}
          onPick={setEventSection}
          onFilter={updateFilter}
        />
      </Box>
      <Box>
        <DataTable
          loading={envLoading || socLoading || govLoading}
          title={`${capitalize(eventSection)} Themes & Events`}
          count={tableData[eventSection].length}
          selectable
          cols={cols}
          rows={tableData[eventSection]}
          paginate
          selection={{
            selectedIds: selectedId ? [selectedId] : [],
            selectToggle: onSelectEvent,
          }}
        />
      </Box>
      <EventSidebar
        open={open}
        onToggle={toggle}
        topicId={companyData?.sentifi_id || 0}
        eventId={selectedId || ''}
        period={filter[eventSection]}
        loading={companyLoading}
        eventType={eventSection}
      />
    </>
  );
};
